import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import { fixPropWarnings } from '../utils/fixPropWarnings';

import ButtonOrLink from './ButtonOrLink';

interface CallToActionProps {
  className?: string;
  variant: 'primary-btn' | 'secondary-btn' | 'tertiary-btn' | string;
  onClick?: () => void;
  onKeyDown?: () => void;
  submit?: boolean;
  href?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

const Button = styled(ButtonOrLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-start;
  border-radius: 28px;
  padding: 10px;
  font-size: 14px;
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  line-height: 18px;
  letter-spacing: 0.2px;
  text-align: center;
  transition: 0.5s all ease-out;
  cursor: pointer;

  &:lang(en) {
    white-space: nowrap;
  }

  :lang(fr) {
    line-height: 13px;
  }

  &:disabled {
    cursor: not-allowed;

    &:hover,
    &:focus {
      cursor: not-allowed;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
    padding: 10px 20px;
  }
`;

const PrimaryButton = styled(Button).withConfig(fixPropWarnings('variant'))`
  border: 2px solid ${themeGet('colors.blacks.1')};
  color: ${themeGet('colors.white')};
  background-color: ${themeGet('colors.blacks.1')};

  :hover,
  :focus {
    background-color: ${themeGet('colors.blacks.1')};
  }
`;

const SecondaryButton = styled(Button).withConfig(fixPropWarnings('variant'))`
  border: 2px solid ${themeGet('colors.blacks.1')};
  color: ${themeGet('colors.blacks.1')};
  background-color: ${themeGet('colors.white')};

  :hover,
  :focus {
    color: ${themeGet('colors.blacks.1')};
  }
`;

const TertiaryButton = styled(Button).withConfig(fixPropWarnings('variant'))`
  border: 2px solid ${themeGet('colors.red')};
  color: ${themeGet('colors.white')};
  background-color: ${themeGet('colors.red')};

  :hover,
  :focus {
    color: ${themeGet('colors.white')};
    background-color: ${themeGet('colors.reds.1')};
  }
`;

const CallToAction = ({
  className,
  variant,
  children,
  onClick,
  onKeyDown,
  submit,
  href,
  disabled,
  ariaLabel,
}: PropsWithChildren<CallToActionProps>) => {
  switch (variant) {
    case 'primary-btn':
      return (
        <PrimaryButton
          className={className}
          onClick={onClick}
          onKeyDown={onKeyDown}
          submit={submit}
          href={href}
          ariaLabel={ariaLabel}
          disabled={disabled}
        >
          {children}
        </PrimaryButton>
      );

    case 'secondary-btn':
      return (
        <SecondaryButton
          className={className}
          onClick={onClick}
          onKeyDown={onKeyDown}
          submit={submit}
          href={href}
          ariaLabel={ariaLabel}
          disabled={disabled}
        >
          {children}
        </SecondaryButton>
      );

    case 'tertiary-btn':
      return (
        <TertiaryButton
          className={className}
          onClick={onClick}
          onKeyDown={onKeyDown}
          submit={submit}
          href={href}
          ariaLabel={ariaLabel}
          disabled={disabled}
        >
          {children}
        </TertiaryButton>
      );

    default:
      return null;
  }
};

export default CallToAction;
